(function () {
  class SearchPopup extends HTMLElement {
    connectedCallback() {
      this._overlay = this.querySelector('#sp-overlay');
      this._modal = this.querySelector('.sp-modal');
      this._input = this.querySelector('.sp-input');
      this._closeBtn = this.querySelector('.sp-close-btn');

      // Predictive search elements
      this._resultsWrap = this.querySelector('#sp-results');
      this._resultsGrid = this.querySelector('#sp-results-grid');
      this._resultsTitle = this.querySelector('#sp-results-title');
      this._resultsViewAll = this.querySelector('#sp-results-viewall');
      this._defaultContent = this.querySelector('#sp-default-content');
      this._loading = this.querySelector('#sp-results .sp-loading');

      // Predictive search state
      this._cache = {};
      this._debounceTimer = null;
      this._abortCtrl = null;
      this._currentQuery = '';

      // Close on clicking the overlay mask (outside the modal)
      this._overlay.addEventListener('click', (e) => {
        if (e.target === this._overlay) this.close();
      });

      // Close button
      this._closeBtn.addEventListener('click', () => this.close());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._overlay.classList.contains('sp-open')) this.close();
      });

      // Open on any [data-search-popup-open] click globally
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-popup-open]')) {
          e.preventDefault();
          this.open();
        }
      });

      // Predictive search input listener
      this._input.addEventListener('input', () => this._onInput());

      // sp-tags: click fills input and triggers ajax search
      this.addEventListener('click', (e) => {
        const tag = e.target.closest('.sp-tag');
        if (!tag) return;
        e.preventDefault();
        this._input.value = tag.textContent.trim();
        this._input.focus();
        this._onInput();
      });

      // Add to cart
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.sp-btn-cart');
        if (!btn) return;
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
          .then(() => {
            if (window.HPBMiniCart) window.HPBMiniCart.refresh({ open: true });
            this.close();
          })
          .catch((err) => console.error('Search popup add-to-cart error:', err))
          .finally(() => { btn.disabled = false; });
      });
    }

    _onInput() {
      clearTimeout(this._debounceTimer);
      const query = this._input.value.trim();
      if (!query) {
        this._showDefault();
        return;
      }
      this._debounceTimer = setTimeout(() => this._doSearch(query), 300);
    }

    async _doSearch(query) {
      if (this._cache[query]) {
        this._renderResults(this._cache[query], query);
        return;
      }

      // Show loading
      this._currentQuery = query;
      this._resultsWrap.style.display = 'block';
      this._resultsWrap.classList.add('sp-loading-state');
      this._resultsGrid.innerHTML = '';
      this._resultsTitle.textContent = '';
      this._defaultContent.classList.add('sp-hidden');

      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=hide&resources[options][fields]=body,title,tag,product_type,variants.title,vendor`;
        const res = await fetch(url, { signal: this._abortCtrl.signal });
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        const raw = data?.resources?.results?.products || [];
        const products = await Promise.all(raw.map(p => this._withFirstVariantPrice({
          title: p.title,
          url: p.url,
          image: p.image,
          price: this._formatMoney(p.price),
          comparePrice: (p.compare_at_price_max && p.compare_at_price_max !== p.price)
            ? this._formatMoney(p.compare_at_price_max) : ''
        }, this._abortCtrl.signal)));
        this._cache[query] = products;
        if (this._currentQuery === query) this._renderResults(products, query);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Search popup error:', err);
          this._showDefault();
        }
      }
    }

    async _withFirstVariantPrice(product, signal) {
      const handle = this._getProductHandle(product.url);
      if (!handle) return product;

      try {
        const rootUrl = (window.T4Sroutes && T4Sroutes.root_url && T4Sroutes.root_url !== '/') ? T4Sroutes.root_url : '';
        const res = await fetch(`${rootUrl}/products/${handle}.js`, { signal });
        if (!res.ok) return product;
        const data = await res.json();
        const variant = data?.variants?.[0];
        if (!variant) return product;

        product.price = this._formatMoneyCents(variant.price);
        product.comparePrice = (variant.compare_at_price && Number(variant.compare_at_price) !== Number(variant.price))
          ? this._formatMoneyCents(variant.compare_at_price) : '';
      } catch (err) {
        if (err.name === 'AbortError') throw err;
      }

      return product;
    }

    _getProductHandle(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      } catch (err) {
        const match = String(url).match(/\/products\/([^/?#]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      }
    }

    _formatMoneyCents(price) {
      if (price == null || price === '') return '';
      const n = Number(price);
      if (isNaN(n)) return String(price);
      return this._formatMoney(n / 100);
    }

    _formatMoney(price) {
      if (price == null || price === '') return '';
      const n = Number(price);
      if (isNaN(n)) return String(price);
      const fmt = (window.T4Sconfigs && T4Sconfigs.moneyFormat) || (window.Shopify && Shopify.money_format) || '${{amount}}';
      const amount = n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amountNoDec = String(Math.floor(n));
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, n.toFixed(2).replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, amountNoDec);
    }

    _renderResults(products, query) {
      this._resultsWrap.classList.remove('sp-loading-state');

      if (!products.length) {
        this._resultsTitle.textContent = '';
        this._resultsGrid.innerHTML = '<p class="sp-no-results">No products found for &ldquo;' + query.replace(/</g, '&lt;') + '&rdquo;</p>';
        this._resultsViewAll.href = '/search?type=product&options[unavailable_products]=last&options[prefix]=last&q=' + encodeURIComponent(query);
        this._resultsWrap.style.display = 'block';
        this._defaultContent.classList.add('sp-hidden');
        return;
      }

      this._resultsTitle.textContent = 'Search Results';
      this._resultsGrid.innerHTML = products.map(p => {
        const hasCompare = p.comparePrice && p.comparePrice !== p.price;
        return `<div class="sp-product-card">
          <a href="${p.url}" class="sp-product-img-link">
            ${p.image ? `<img src="${p.image}" alt="${p.title.replace(/"/g, '&quot;')}" width="300" height="300" loading="lazy">` : ''}
          </a>
          <div class="sp-product-info">
            <a href="${p.url}" class="sp-product-title">${p.title}</a>
            <div class="sp-product-price">
              <span class="sp-price-sale">${p.price}</span>
              ${hasCompare ? `<span class="sp-price-compare">${p.comparePrice}</span>` : ''}
            </div>
          </div>
        </div>`;
      }).join('');

      this._resultsViewAll.href = `/search?type=product&options[unavailable_products]=last&options[prefix]=last&q=${encodeURIComponent(query)}`;
      this._resultsWrap.style.display = 'block';
      this._defaultContent.classList.add('sp-hidden');
    }

    _showDefault() {
      if (this._abortCtrl) { this._abortCtrl.abort(); this._abortCtrl = null; }
      this._currentQuery = '';
      this._resultsWrap.classList.remove('sp-loading-state');
      this._resultsWrap.style.display = 'none';
      this._resultsGrid.innerHTML = '';
      this._defaultContent.classList.remove('sp-hidden');
    }

    open() {
      this._overlay.classList.add('sp-open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => { if (this._input) this._input.focus(); }, 60);
    }

    close() {
      this._overlay.classList.remove('sp-open');
      document.body.style.overflow = '';
      this._input.value = '';
      this._showDefault();
    }
  }

  if (!customElements.get('search-popup')) {
    customElements.define('search-popup', SearchPopup);
  }
})();
